import {
  BalanceData, ChestResult, Fix64, SHOP_CURRENCY, marksForGems, GearItem, OfferScreen, ShopId, ShopPurchase, ShopSlot, ShopsJson, ShopsState, ShopCurrency, buyShopSlot, merchantRefreshCost, periodOf,
  purchasesAllowed, refreshMerchant, shopStock, showsOff,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * The Shops screen's use cases (docs/SHOP.md §9; `LevelCatalog.shops`): the Mystery Merchant (Marks), the Event shop
 * (Marks) and the VIP shop (gems) as the player sees them, buying a slot as shown, the Merchant's refresh, and Marks
 * from play. Earned currencies only (no real money moves here), so the shops work on the web too. Hub only, like every
 * purchase (the never list). The state lives in the `shops` save section.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const shopsSection = registerSaveSection<ShopsJson>("shops", asObject, (v) => v);

export interface ShopsDeps {
  readonly catalog: LevelCatalog;
  readonly state: ShopsState;
  /** The player's VIP level (the VIP shop's gate). */
  vipLevel(): number;
  /** Free Merchant refreshes a UTC day at that level. */
  freeRefreshes(): number;
}

/** One slot as the page draws it. */
export interface ShopSlotView extends ShopSlot {
  /** Purchases left this period ("Left: n"; 0 = the sold-out state). */
  readonly left: number;
  /** Whether the discount badge shows. */
  readonly showOff: boolean;
  /** VIP shop: the entry's VIP level is not reached yet (drawn greyed with "VIP n"). */
  readonly locked: boolean;
  /** Why it cannot be bought now ("" when it can). */
  readonly blocked: string;
}

export interface ShopView {
  readonly shop: ShopId;
  readonly currency: ShopCurrency;
  /** The player's balance of `currency`. */
  readonly balance: number;
  /** The restock time (00:00 UTC for the Merchant, Monday 00:00 UTC otherwise): a real countdown. */
  readonly endsAt: number;
  readonly unlocked: boolean;
  readonly slots: readonly ShopSlotView[];
  /** Merchant only: what the next refresh costs (0 = a free VIP refresh; null = none left today). */
  readonly refreshCost: number | null;
}

export interface ShopBuy extends ShopPurchase {
  /** Gear from the chests the purchase paid (banked into the inventory; bound when bought with gems). */
  readonly bankedGear: readonly GearItem[];
  /** Each chest the purchase paid with the gear it held, in order: the chest reveal plays one per chest. */
  readonly opened: readonly { readonly chest: ChestResult; readonly gear: readonly GearItem[] }[];
}

export class Shops {
  constructor(private readonly d: ShopsDeps) {}

  private get balance(): BalanceData { return this.d.catalog.balance; }
  get state(): ShopsState { return this.d.state; }
  /** The level the shops price and stock for: the highest cleared, at least 1. */
  get level(): number { return Math.max(1, this.d.catalog.highestLevelCleared); }

  /** The player's VIP level (the VIP shop's gate and the Merchant's free refreshes). */
  get vipLevel(): number { return this.d.vipLevel(); }
  /** Free Merchant refreshes a UTC day at the player's VIP level. */
  get freeRefreshes(): number { return this.d.freeRefreshes(); }

  static stateFromSave(save: HasSections): ShopsState { return ShopsState.fromJson(shopsSection.read(save)); }
  applyTo(save: HasSections): void { shopsSection.write(save, this.d.state.toJson()); }

  /** Whether a shop is open: the Merchant and the Event shop from their unlock level, the VIP shop from VIP 1. */
  unlocked(shop: ShopId): boolean {
    const cleared = this.d.catalog.highestLevelCleared, s = this.balance.shops;
    switch (shop) {
      case "merchant": return cleared >= s.merchant.unlockAfterLevel;
      case "event": return cleared >= s.event.unlockAfterLevel;
      case "vip": return this.d.vipLevel() >= 1 && s.vip.stock.length > 0;
    }
  }

  view(shop: ShopId, now: number): ShopView {
    const wallet = this.d.catalog.economy.wallet, st = this.d.state;
    const slots = shopStock(this.balance, st, shop, this.d.catalog.playerId, now, this.level).map((s): ShopSlotView => {
      const left = Math.max(0, s.limit - st.boughtCount(shop, s.index, now));
      const locked = shop === "vip" && this.d.vipLevel() < s.vipLevel;
      const blocked = left <= 0 ? "sold out" : locked ? `VIP ${s.vipLevel}` : wallet.balance(s.currency) < s.price ? "not enough" : !this.d.catalog.canPersist() ? NOT_SAVING_HERE : "";
      return { ...s, left, showOff: showsOff(this.balance, s.off), locked, blocked };
    });
    const cur = slots[0]?.currency ?? SHOP_CURRENCY[shop];
    st.roll(now);
    return {
      shop, currency: cur, balance: wallet.balance(cur), endsAt: periodOf(shop === "merchant" ? "daily" : "weekly", now).endsAt ?? now,
      unlocked: this.unlocked(shop), slots,
      refreshCost: shop === "merchant" ? merchantRefreshCost(this.balance, st.merchant.freeUsed, st.merchant.paidUsed, this.d.freeRefreshes()) : null,
    };
  }

  /** Buys a slot as shown (hub only; refused when locked, sold out, short of the currency, or not saving). */
  buy(shown: ShopSlot, now: number, screen: OfferScreen = "hub", gearSlots: readonly number[] = []): ShopBuy | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.unlocked(shown.shop)) return "locked";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const c = this.d.catalog;
    const r = buyShopSlot(this.balance, this.d.state, shown, now, {
      target: { profile: c.profile, wallet: c.economy.wallet, playerId: c.playerId, level: this.level, at: now, ref: "", ownsSkin: () => false },
      level: this.level, vipLevel: this.d.vipLevel(), gearSlots,
    });
    if (typeof r === "string") return r;
    const bankedGear: GearItem[] = [], opened: { chest: ChestResult; gear: GearItem[] }[] = [];
    // Gear from a gem-bought chest is bound (market.ts: gems never turn into Marks); earned currencies' chests pay as any chest.
    for (const chest of r.granted.chests) {
      const gear = c.bankChest(chest);
      for (const g of gear) { if (shown.shop === "vip") g.bound = true; bankedGear.push(g); }
      opened.push({ chest, gear });
    }
    return { ...r, bankedGear, opened };
  }

  /** The Merchant's refresh (hub only): free VIP refreshes first, then gems. Returns the gems paid. */
  refresh(now: number, screen: OfferScreen = "hub"): number | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.unlocked("merchant")) return "locked";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const c = this.d.catalog;
    return refreshMerchant(this.balance, this.d.state, { profile: c.profile, wallet: c.economy.wallet, playerId: c.playerId, level: this.level, at: now, ref: "", ownsSkin: () => false }, now, this.d.freeRefreshes());
  }

  /**
   * The hub's Shops badge: featured Event slots the player can buy right now (Marks enough, in stock). A count on a
   * button the player taps, never a pop-up or a push (the quiet rules).
   */
  badge(now: number): number {
    if (!this.unlocked("event")) return 0;
    return this.view("event", now).slots.filter((s) => s.featured && s.blocked === "").length;
  }

  /**
   * 1 while the Merchant's free item of the day (MP-08) is still on its shelf: the red dot on the Merchant tab and on
   * the hub's Shop tab (docs/SHOP.md §20: anything free carries a red dot until it is taken).
   */
  merchantFree(now: number): number {
    if (!this.unlocked("merchant")) return 0;
    return this.view("merchant", now).slots.some((s) => s.free && s.left > 0) ? 1 : 0;
  }

  /**
   * Grants the Marks event play pays (a Proving Grounds node, the Trial, a Heroic first win: shops.json event.sources,
   * a gem value paid at the player's level's Marks rate, meta/shops.ts marksForGems); returns them.
   */
  creditEventMarks(gemValue: Fix64, now: number, ref: string): number {
    return this.payEventMarks(marksForGems(this.balance, gemValue, this.level), now, ref);
  }

  private payEventMarks(marks: number, now: number, ref: string): number {
    return this.d.catalog.economy.wallet.grant("marks", marks, "eventPlay", now, ref) ? marks : 0;
  }

  /** A farm win (a won replay of a cleared level): its Marks, up to farmWinsPerDay a UTC day. Returns what it paid. */
  farmWin(now: number, ref: string): number {
    const v = this.d.state.farmMarks(this.balance, now, this.level);
    return v > 0 ? this.payEventMarks(v, now, ref) : 0;
  }
}
